import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRooms } from "@/hooks/useRooms";
import { RoomsGrid } from "@/components/rooms/RoomsGrid";
import { useToast } from "@/hooks/use-toast";
import { Clock, Calendar, ArrowDownAZ, ArrowUpZA, ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "@/context/LanguageContext";

type SortOption = 'newest' | 'oldest' | 'name-asc' | 'name-desc';

export function MyRoomsSection() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const {
    rooms,
    loading,
    fetchRooms,
    createRoom,
    renameRoom,
    deleteRoom,
    changeVisibility,
  } = useRooms();

  const getSortLabel = (sort: SortOption): string => {
    switch (sort) {
      case 'newest': return t("dashboard.newest");
      case 'oldest': return t("dashboard.oldest");
      case 'name-asc': return t("dashboard.nameAZ");
      case 'name-desc': return t("dashboard.nameZA");
    }
  };

  const getSortIcon = (sort: SortOption) => {
    switch (sort) {
      case 'newest': return Clock;
      case 'oldest': return Calendar;
      case 'name-asc': return ArrowDownAZ;
      case 'name-desc': return ArrowUpZA;
    }
  };

  const sortedRooms = useMemo(() => {
    return [...rooms].sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        default:
          return 0;
      }
    });
  }, [rooms, sortBy]);


  const handleCreate = async () => {
    const room = await createRoom();
    if (room) {
      toast({
        title: "Room created",
        description: "Your new room has been created successfully.",
      });
      navigate(`/room/${room.id}`);
    }
  };

  const handleRename = async (id: string, name: string) => {
    await renameRoom(id, name);
    toast({
      title: "Room renamed",
      description: "Your room has been renamed successfully.",
    });
  };

  const handleDelete = async (id: string) => {
    await deleteRoom(id);
    toast({
      title: "Room deleted",
      description: "Your room has been deleted successfully.",
    });
  };

  const SortIcon = getSortIcon(sortBy);

  return (
    <div className="w-full mb-10">
      <div className="mb-4">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-foreground">{t("dashboard.myRooms")}</span>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 h-7 px-2 rounded-full bg-transparent text-primary/60 text-xs hover:bg-primary/5 dark:hover:bg-primary/10 transition-all">
                <SortIcon className="h-4 w-4 mr-1" />
                <span>{getSortLabel(sortBy)}</span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-36">
              <DropdownMenuItem onClick={() => setSortBy('newest')} className="gap-2">
                {sortBy === 'newest' ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                <Clock className="h-4 w-4" />
                {t("dashboard.newest")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('oldest')} className="gap-2">
                {sortBy === 'oldest' ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                <Calendar className="h-4 w-4" />
                {t("dashboard.oldest")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('name-asc')} className="gap-2">
                {sortBy === 'name-asc' ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                <ArrowDownAZ className="h-4 w-4" />
                {t("dashboard.nameAZ")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy('name-desc')} className="gap-2">
                {sortBy === 'name-desc' ? <Check className="h-4 w-4" /> : <span className="w-4" />}
                <ArrowUpZA className="h-4 w-4" />
                {t("dashboard.nameZA")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground mt-4">Loading rooms...</p>
      ) : (
        <RoomsGrid
          rooms={sortedRooms}
          onCreateRoom={handleCreate}
          onRenameRoom={handleRename}
          onDeleteRoom={handleDelete}
          onChangeVisibility={changeVisibility}
        />
      )}
    </div>
  );
}
